import React, { useState, useEffect } from 'react';
import { User, MapPin, Briefcase, Shield, FileText } from 'lucide-react';
import api from '../../utils/api';
import useAuthStore from '../../context/authStore';
import { formatDate } from '../../utils/dateUtils';

export default function MemberProfilePage() {
  const { user } = useAuthStore();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/member/profile').then((res) => {
      setProfile(res.data.profile);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 190, color: 'var(--text-muted)', fontSize: 13, flexShrink: 0 }}>{label}</div>
      <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  ) : null;

  const statusColors = {
    ACTIVE: 'active', PENDING: 'pending',
    REFERRAL_PENDING: 'approved', REJECTED: 'rejected', PAYMENT_RECEIVED: 'payment'
  };

  let scannedDegrees = [];
  try {
    if (profile?.scannedDegrees) scannedDegrees = JSON.parse(profile.scannedDegrees);
  } catch { scannedDegrees = []; }

  return (
    <div>
      <div className="page-header">
        <h2>My Profile</h2>
        <p>Your membership information and application status</p>
      </div>

      {/* Header Card */}
      <div className="card" style={{ padding: 28, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 20 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: profile?.profilePhoto ? 'transparent' : 'var(--primary)',
            overflow: 'hidden', flexShrink: 0, border: '3px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {profile?.profilePhoto ? (
              <img src={`https://demo11.stsgram.com/${profile.profilePhoto}`}
                alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: 'white', fontSize: 24, fontWeight: 700 }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            )}
          </div>
          <div>
            <h3 style={{ color: 'var(--primary)', fontSize: 20 }}>
              {user?.firstName} {user?.middleName} {user?.lastName}
            </h3>
            <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{user?.email}</div>
            {profile?.memberId && (
              <div style={{ marginTop: 6 }}>
                <span style={{
                  background: 'var(--primary)', color: 'white',
                  padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700
                }}>
                  ID: {profile.memberId}
                </span>
                {profile.membershipType && (
                  <span style={{
                    background: '#dbeafe', color: '#1e40af',
                    padding: '3px 12px', borderRadius: 20, fontSize: 12,
                    fontWeight: 600, marginLeft: 8
                  }}>
                    {profile.membershipType}
                  </span>
                )}
              </div>
            )}
          </div>
          {profile?.status && (
            <div style={{ marginLeft: 'auto' }}>
              <span className={`badge badge-${statusColors[profile.status] || 'pending'}`}>
                {profile.status.replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </div>
        <Row label="Mobile" value={user?.mobile} />
        {/* DD/MM/YYYY format */}
        <Row label="Member Since" value={formatDate(profile?.createdAt)} />
      </div>

      {profile && (
        <>
          {/* Personal Information */}
          <div className="card" style={{ padding: 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <User size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Personal Information</h3>
            </div>
            {/* DD/MM/YYYY format */}
            <Row label="Date of Birth" value={formatDate(profile.dateOfBirth)} />
            <Row label="Age" value={profile.age ? `${profile.age} years` : null} />
            <Row label="Gender" value={profile.gender} />
          </div>

          {/* Contact Information */}
          <div className="card" style={{ padding: 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <MapPin size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Contact Information</h3>
            </div>
            <Row label="Residential Address" value={profile.address} />
            <Row label="City" value={profile.city} />
            <Row label="State" value={profile.state} />
            <Row label="Nationality" value={profile.nationality} />
            <Row label="Pin Code" value={profile.pincode} />
            <Row label="Phone" value={profile.phone} />
          </div>

          {/* Professional Information */}
          <div className="card" style={{ padding: 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Briefcase size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Professional Information</h3>
            </div>
            <Row label="Degree" value={profile.degree} />
            <Row label="University" value={profile.university} />
            <Row label="Year of Passing" value={profile.yearOfPassing} />
            <Row label="Areas of Expertise" value={profile.areasOfInterest} />
            <Row label="Clinic / Practice Address" value={profile.officeAddress} />
            <Row label="Membership Type" value={profile.membershipType} />
          </div>

          {/* Scanned Documents */}
          {scannedDegrees.length > 0 && (
            <div className="card" style={{ padding: 28, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <FileText size={18} style={{ color: 'var(--primary)' }} />
                <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Uploaded Documents</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {scannedDegrees.map((doc, idx) => (
                  <a key={idx} href={`https://demo11.stsgram.com/${doc}`}
                    target="_blank" rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', background: '#dbeafe',
                      borderRadius: 8, textDecoration: 'none',
                      color: 'var(--primary)', fontSize: 13, fontWeight: 500
                    }}>
                    📄 Document {idx + 1} — Click to View
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Referral Status */}
          <div className="card" style={{ padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Shield size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Referral Status</h3>
            </div>
            {profile.referralApprovals?.map((approval) => (
              <div key={approval.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 0', borderBottom: '1px solid var(--border)'
              }}>
                <div>
                  <div style={{ fontSize: 14 }}>Referral {approval.referralNumber}</div>
                  {approval.approvedAt && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {/* DD/MM/YYYY format */}
                      Approved on: {formatDate(approval.approvedAt)}
                    </div>
                  )}
                </div>
                <span className={`badge badge-${approval.approved ? 'active' : 'pending'}`}>
                  {approval.approved ? '✓ Approved' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {!profile && (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <User size={48} style={{ color: 'var(--border)', margin: '0 auto 16px' }} />
          <h3 style={{ color: 'var(--text-muted)' }}>No membership registration found</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 8 }}>
            Please complete your membership registration to see profile details.
          </p>
        </div>
      )}
    </div>
  );
}
